import React, { useEffect, useMemo, useState } from 'react';
import { Image, Linking, Platform, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useTheme } from '../context/ThemeContext';
import { analytics } from '../services/analytics';
import { ANDROID_MARKET_LABEL, ANDROID_MARKET_URLS, STORE_LABEL, STORE_URLS, androidOpenInAppUrl, detectMobileStore, isAndroidAppInstalled, isIosSafari, type AndroidMarket, type MobileStore } from './installBanner';

import { ON_ACCENT_TEXT } from '../styles/accentText';
/**
 * CORE-1116 — предложение поставить приложение, в мобильной веб-версии.
 *
 * Полоса сверху, а не нижняя модалка: по задаче — «компактный install-banner в
 * верхней части экрана, bottom sheet не использовать». Разница не косметическая:
 * лист снизу перекрывает то, ради чего человек пришёл, и требует его закрыть,
 * а полоса просто есть.
 *
 * Живёт ВНУТРИ шапки, первой строкой её фиксированного блока. Так у верхнего
 * слоя остаётся одна высота на всех: шапка меряет себя целиком и объявляет
 * занятую полосу (CORE-1120), а те, кто под неё подстраивается, получают
 * правильное число без единой правки. Отдельный фиксированный слой поверх
 * шапки означал бы второе смещение, которое каждый экран обязан прибавлять сам,
 * — и первый же забывший дал бы ровно то перекрытие, которое задача запрещает.
 *
 * Ничего не затемняет и ничего не блокирует: закрыл — и дальше работает
 * веб-версия.
 */

/**
 * Закрытие держится в памяти вкладки, а не в постоянном хранилище: по задаче
 * баннер не показывается «в рамках текущей сессии», а не навсегда. Модульная
 * переменная переживает переходы между экранами, `sessionStorage` — ещё и
 * перезагрузку той же вкладки, но умирает вместе с ней.
 */
const DISMISS_KEY = 'agape_install_banner_dismissed_v1';
let dismissedInMemory = false;

function readDismissed(): boolean {
  if (dismissedInMemory) return true;
  if (Platform.OS !== 'web') return false;
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    // Приватный режим и запрет сторонних данных бросают прямо на чтении.
    return false;
  }
}

function rememberDismissed(): void {
  dismissedInMemory = true;
  if (Platform.OS !== 'web') return;
  try {
    window.sessionStorage.setItem(DISMISS_KEY, '1');
  } catch { /* не смогли запомнить — в памяти вкладки всё равно закрыто */ }
}

/** Тест-хелпер: модульное состояние между тестами само не сбрасывается. */
export function resetInstallBannerDismissal(): void {
  dismissedInMemory = false;
}

export const InstallAppBanner: React.FC = () => {
  const { colors, isDark } = useTheme();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();

  const store = useMemo<MobileStore | null>(() => {
    // Веб-только по существу: в приложении такое предложение бессмысленно.
    if (Platform.OS !== 'web' || typeof navigator === 'undefined') return null;
    const touch = navigator.maxTouchPoints ?? 0;
    // CORE-1723 — в Safari на iOS приложение предлагает сама система (Smart
    // App Banner): «Открыть» или «Загрузить». Второй баннер ей противоречил.
    if (isIosSafari(navigator.userAgent, touch)) return null;
    return detectMobileStore(navigator.userAgent, touch);
  }, []);

  const [hidden, setHidden] = useState(() => readDismissed());
  // CORE-1723 — приложение уже на телефоне: «Открыть в приложении», и кнопка
  // открывает его на том же экране. Ответ знает только Chrome на Android; где
  // ответа нет, остаётся «Скачать приложение» и стор.
  const [installed, setInstalled] = useState(false);
  // CORE-1965 — у Android два магазина: «Скачать приложение» сначала
  // раскрывает выбор, и уходит человек уже по кнопке магазина. Распознанная
  // установка выбора не показывает — там «Открыть в приложении».
  const [choosing, setChoosing] = useState(false);

  useEffect(() => {
    if (!store || hidden) return;
    analytics.track('install_banner_shown', { store });
    // Ровно один раз на появление: зависимость от `hidden` здесь и нужна, чтобы
    // повторный показ после закрытия не считался новым.
  }, [store, hidden]);

  useEffect(() => {
    if (store !== 'android' || hidden) return;
    let alive = true;
    void isAndroidAppInstalled(navigator).then((found) => {
      if (alive) setInstalled(found);
    });
    return () => {
      alive = false;
    };
  }, [store, hidden]);

  if (!store || hidden) return null;

  const showChoice = store === 'android' && choosing && !installed;
  // Длинная подпись Android-кнопки на телефоне уже 360 px не оставляет места
  // названию: там остаются иконка, кнопка и крестик. iOS не меняли. С
  // открытым выбором кнопки в строке нет — и место для текста есть.
  const textless = store === 'android' && width < 360 && !showChoice;
  const label = store === 'android'
    ? (installed ? 'Открыть в приложении' : 'Скачать приложение')
    : 'Скачать';

  const open = () => {
    if (installed && Platform.OS === 'web') {
      analytics.track('install_banner_open_clicked', { store });
      window.location.href = androidOpenInAppUrl(window.location.pathname + window.location.search);
      return;
    }
    if (store === 'android') {
      analytics.track('install_banner_store_choice_shown', { store });
      setChoosing(true);
      return;
    }
    analytics.track('install_banner_download_clicked', { store });
    void Linking.openURL(STORE_URLS[store]);
  };

  // Уход в магазин — то же событие, что и раньше, плюс какой из двух выбран.
  const download = (market: AndroidMarket) => {
    analytics.track('install_banner_download_clicked', { store, market });
    void Linking.openURL(ANDROID_MARKET_URLS[market]);
  };

  const dismiss = () => {
    analytics.track('install_banner_dismissed', { store });
    rememberDismissed();
    setHidden(true);
  };

  return (
    <View
      testID="install-app-banner"
      style={[
        styles.bar,
        {
          // Safe area сверху: при `viewport-fit=cover` полоса иначе уезжает под
          // вырез, и первым скрывается крестик у самого края.
          paddingTop: 10 + insets.top,
          backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#FFF7F1',
          borderBottomColor: colors.borderSubtle,
        },
      ]}
    >
      <View style={styles.row}>
        <Image source={require('../../assets/icon.png')} style={styles.icon} />

        {/* Без текста (узкий Android) колонка остаётся пустой: держит кнопку с
            крестиком у правого края. */}
        <View style={styles.copy}>
          {!textless && (
            <>
              <Text numberOfLines={1} style={[styles.name, { color: colors.textPrimary }]}>
                Agapella
              </Text>
              {/* Одна строка и без восклицаний: полоса объясняет, зачем ставить, и
                  не уговаривает. Название стора живёт в подписи кнопки для читалки —
                  в самой строке оно съело бы место, которого на 320 px нет. */}
              <Text numberOfLines={1} style={[styles.note, { color: colors.textMuted }]}>
                {showChoice ? 'Выберите магазин' : 'Практики и Live-сессии под рукой'}
              </Text>
            </>
          )}
        </View>

        {!showChoice && (
          <Pressable
            onPress={open}
            accessibilityRole="button"
            accessibilityLabel={installed
              ? 'Открыть эту страницу в приложении Agapella'
              : `Скачать приложение Agapella в ${STORE_LABEL[store]}`}
            testID="install-app-banner-download"
            style={({ pressed }) => [styles.cta, { opacity: pressed ? 0.85 : 1 }]}
          >
            <Text style={styles.ctaLabel}>{label}</Text>
          </Pressable>
        )}

        <Pressable
          onPress={dismiss}
          accessibilityRole="button"
          accessibilityLabel="Закрыть предложение установить приложение"
          testID="install-app-banner-close"
          hitSlop={10}
          style={styles.close}
        >
          <Ionicons name="close" size={18} color={colors.textMuted} />
        </Pressable>
      </View>

      {/* CORE-1965 — выбор магазина второй строкой той же полосы, а не листом
          снизу: полоса по-прежнему ничего не затемняет, а шапка меряет себя
          целиком и сама отдаёт экранам новую высоту. Крестик выше закрывает
          всё предложение — и дальше работает веб-версия. */}
      {showChoice && (
        <View style={styles.choice} testID="install-app-banner-stores">
          {(['play', 'rustore'] as const).map((market) => (
            <Pressable
              key={market}
              onPress={() => download(market)}
              accessibilityRole="button"
              accessibilityLabel={`Скачать приложение Agapella в ${ANDROID_MARKET_LABEL[market]}`}
              testID={`install-app-banner-store-${market}`}
              style={({ pressed }) => [styles.cta, { opacity: pressed ? 0.85 : 1 }]}
            >
              <Text style={styles.ctaLabel}>{ANDROID_MARKET_LABEL[market]}</Text>
            </Pressable>
          ))}
        </View>
      )}
    </View>
  );
};

const SOLAR = '#E98245';

const styles = StyleSheet.create({
  bar: {
    paddingBottom: 10,
    paddingHorizontal: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  // Кнопки магазинов — под текстом, от его левого края (иконка 36 + зазор 10).
  choice: { flexDirection: 'row', gap: 10, marginTop: 10, marginLeft: 46 },
  icon: { width: 36, height: 36, borderRadius: 9 },
  // `minWidth: 0` — иначе длинная подпись растягивает строку и выдавливает
  // кнопку с крестиком за правый край узкого экрана.
  copy: { flex: 1, minWidth: 0 },
  name: { fontSize: 14, fontWeight: '700' },
  note: { fontSize: 12, marginTop: 1 },
  cta: {
    backgroundColor: SOLAR,
    borderRadius: 999,
    paddingHorizontal: 16,
    paddingVertical: 8,
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as object) : null),
  },
  ctaLabel: { color: ON_ACCENT_TEXT, fontSize: 13, fontWeight: '700' },
  close: {
    padding: 4,
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as object) : null),
  },
});

export default InstallAppBanner;
